import { forwardRef, type CSSProperties } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIsAppLocaleReady } from 'features/i18n/useIsAppLocaleReady';
import { pageBackgroundSx } from 'features/theme/theme';
import { OnBackgroundText } from 'features/ui';

type LoadingScreenProps = {
  title?: string | undefined;
  description?: string | undefined;
  /** Shows the text before the signed-in user's language is active. */
  textVisible?: boolean;
  lang?: string | undefined;
  style?: CSSProperties;
};

const SPINNER_SIZE_PX = 64;

export const LoadingScreen = forwardRef<HTMLDivElement, LoadingScreenProps>(
  ({ title, description, textVisible = false, lang, style }, ref) => {
    const { t } = useTranslation();
    const showText = useIsAppLocaleReady() || textVisible;

    return (
      <Box
        ref={ref}
        style={style}
        lang={lang}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={theme => ({
          ...pageBackgroundSx(theme),
          position: 'fixed',
          inset: 0,
          zIndex: theme.zIndex.drawer,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        })}
      >
        <CircularProgress aria-hidden="true" size={SPINNER_SIZE_PX} />
        <Box
          data-testid="loading-text-slot"
          sx={theme => ({
            position: 'absolute',
            top: `calc(50% + ${String(SPINNER_SIZE_PX / 2)}px)`,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 480,
            px: 3,
            textAlign: 'center',
            opacity: showText ? 1 : 0,
            transition: theme.transitions.create('opacity', { duration: 250 }),
            pointerEvents: showText ? 'auto' : 'none',
          })}
        >
          <Typography variant="h5" component="h1" sx={{ mt: 3 }}>
            <OnBackgroundText>{title ?? t('Loading')}</OnBackgroundText>
          </Typography>
          {description !== undefined && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              <OnBackgroundText multiline>{description}</OnBackgroundText>
            </Typography>
          )}
        </Box>
      </Box>
    );
  }
);

LoadingScreen.displayName = 'LoadingScreen';
